
import { Label, Suggestion, IngestDocument } from '@/model';
import Vue from 'vue';
import labels from '@/store/labels';
import LabelPanel from '@/components/LabelPanel.vue';
import { groupBy } from '@/util/array-util';

const happyRobotColors = ['green', 'yellow', 'red', 'blue', 'purple'];

export default Vue.extend({
  name: 'SuggestionStepper',
  components: {
    LabelPanel,
  },
  props: {
    suggestions: { type: Array as () => Suggestion[] },
    ingest: { type: Object as () => IngestDocument | undefined, required: false },
    value: { type: Object as () => Suggestion | undefined },
  },
  data() {
    return {
      suggestion: undefined as Suggestion | undefined,
      showAll: false,
      confidenceThreshold: 0.65,
      showJumpList: false,
      happyRobotCycle: 0,
      happyRobotColor: happyRobotColors[0],
      index: 0 as number,
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(value: Suggestion | undefined) {
        if (value) {
          this.index = Math.max(this.filterdSuggestions.findIndex((s) => s.id === value.id), 0);
        } else {
          this.change(this.filterdSuggestions[0]);
        }
      },
    },
  },

  computed: {
    boxPages(): Map<number, number> {
      const pages = new Map<number, number>();
      this.ingest?.pages.forEach((page, index) => {
        page.boxes.forEach((box) => {
          pages.set(box.id, index + 1);
        });
      });
      return pages;
    },
    boxSeqNos(): Map<number, number> {
      const seqNos = new Map<number, number>();
      this.ingest?.pages.forEach((page) => {
        page.boxes.forEach((box) => {
          seqNos.set(box.id, box.seqNo);
        });
      });
      return seqNos;
    },
    filterdSuggestions(): Suggestion[] {
      const suggestions = this.showAll === true ? this.suggestions : this.suggestions.filter(this.validSuggestion);
      return [...suggestions].sort((a, b) => {
        const pageA = this.boxToPage(a.boxId);
        const pageB = this.boxToPage(b.boxId);
        if (pageA !== pageB) return pageA - pageB;
        return (this.boxSeqNos.get(a.boxId) ?? 0) - (this.boxSeqNos.get(b.boxId) ?? 0);
      });
    },
    suggestionsPerPage(): {pageId: number, suggestions: readonly Suggestion[]}[] {
      const pages = groupBy(this.filterdSuggestions, (s) => this.boxToPage(s.boxId));
      return Array.from(pages.entries()).map(([pageId, suggestions]) => ({ pageId, suggestions }));
    },
    label(): Label | undefined {
      if (this.value) {
        return labels.state.labels?.find((l) => l.id === this.value?.labelId);
      }
      return undefined;
    },
    actionAvailable(): boolean {
      return this.value?.action === undefined;
    },
    showConfidence(): boolean {
      return this.value?.confidence !== undefined;
    },
    approved(): boolean | undefined {
      if (this.value?.action !== undefined) {
        return this.value.action.fragmentId !== undefined;
      }
      return undefined;
    },
    nextAfterLastApproved(): Suggestion | undefined {
      const suggestions = this.filterdSuggestions;
      let lastApprovedIndex = -1;
      suggestions.forEach((s, i) => {
        if (s.action?.fragmentId !== undefined) {
          lastApprovedIndex = i;
        }
      });
      return suggestions[lastApprovedIndex + 1];
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    saveSuggestionAction(accept: boolean) {
      if (this.value) {
        this.$emit('saveSuggestionAction', this.value, accept);
      }
    },
    removeAction() {
      if (this.value) {
        this.$emit('removeAction', this.value);
      }
    },
    confidence(value: number | undefined) {
      if (value === undefined) {
        return '';
      }
      return Math.round(value * 100);
    },
    boxToPage(boxId: number): number {
      return this.boxPages.get(boxId) || 0;
    },
    change(suggestion: Suggestion) {
      this.$emit('change', suggestion);
    },
    labelLookup(labelId: number | undefined): Label | undefined {
      return labels.state.labels?.find((l) => l.id === labelId);
    },
    validSuggestion(suggestion: Suggestion): boolean {
      return suggestion.confidence !== undefined && suggestion.confidence > this.confidenceThreshold;
    },
    happyRobotCycleChange() {
      this.happyRobotColor = happyRobotColors[
        ++this.happyRobotCycle % happyRobotColors.length
      ];
    },

    next() {
      this.change(this.filterdSuggestions[this.index + 1]);
    },

    previous() {
      this.change(this.filterdSuggestions[this.index - 1]);
    },

    jumpToNextAfterLastApproved() {
      if (this.nextAfterLastApproved) {
        this.change(this.nextAfterLastApproved);
      }
    },

    statusLabel(suggestion: Suggestion): string {
      if (suggestion.action === undefined) {
        return 'Pending';
      }
      return suggestion.action.fragmentId !== undefined ? 'True positive' : 'False positive';
    },

    statusColor(suggestion: Suggestion): string {
      if (suggestion.action === undefined) {
        return 'grey';
      }
      return suggestion.action.fragmentId !== undefined ? 'green' : 'red';
    },

    handleKeydown(event: KeyboardEvent) {
      if (event.shiftKey && event.key === 'ArrowDown' && this.index < this.suggestions.length) {
        this.next();
        event.preventDefault();
      }
      if (event.shiftKey && event.key === 'ArrowUp' && this.index > 0) {
        this.previous();
        event.preventDefault();
      }
      if (event.ctrlKey && event.key === 'r' && this.actionAvailable) {
        this.saveSuggestionAction(false);
        event.preventDefault();
      }
    },

  },
});
